import {
  IssueType,
  replaceMentionPlaceholderWithNames,
  Severity,
} from '@gripp/shared-logic';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { TypeIcon, type TypeVariant } from '../crop-circle/Components/Type';
import { typeColors } from '../crop-circle/Components/Type/types';
import { Icon } from '../crop-circle/icons';
import { colors as ccColors } from '../crop-circle/tokens/colors';
import { ActivityOptionsSheet } from './activityOptionsSheet';
import type { IssueHeaderProps } from './issueHeader';
import { IssueHeaderReminder } from './issueHeaderReminder';

// Mapping between Severity and TypeVariant for crop circle redesign
const severityToVariant: Record<Severity, TypeVariant> = {
  [Severity.Red]: 'urgent',
  [Severity.Yellow]: 'pending',
  [Severity.Green]: 'solved',
  [Severity.Purple]: 'note',
  [Severity.Blue]: 'routines',
};

// Single-row top bar per Figma V6 "Phone/Video Call v0.2" (node 9830:28634):
// back chevron | type badge + title + asset subtitle (one tap target opening
// activity settings) | call icon | overflow menu. Replaces the previous
// two-row header (back/asset nav bar + floating card with TypeDropdown).
// Severity is display-only here; editing moved to the activity settings page.
// Gated behind the Communication Platform feature toggle — see issueHeader.tsx.
export const IssueHeaderCompact = (props: IssueHeaderProps) => {
  const { t } = useTranslation();
  const issue = props.issue;
  // Chat-options sheet (kebab toggle), dropping down below the bar.
  const [showOptions, setShowOptions] = useState(false);

  const getProcessedTitle = () => {
    const titleText = issue.title;

    // Process mentions to ensure clean format
    if (titleText.includes('@[') && (issue as any).userMentions) {
      return replaceMentionPlaceholderWithNames(
        titleText,
        (issue as any).userMentions,
        t('mentions.unknownUser'),
        true
      );
    }

    return titleText;
  };

  const isAccessRequest = issue?.type === IssueType.AccessRequest;

  // Campaign Issues always render the pink 'campaign' variant regardless of
  // severity (which is BLUE on dispatch); Access Request issues render the
  // orange 'notification' variant; everything else maps from severity.
  const typeVariant: TypeVariant =
    issue?.type === IssueType.Campaign
      ? 'campaign'
      : isAccessRequest
      ? 'notification'
      : severityToVariant[issue.severity];

  const handleBackPress = () => {
    if (props.onBackPress) {
      props.onBackPress();
    }
  };

  if (!issue) {
    return;
  }

  const canOpenSettings = !!props.onEditPress && !isAccessRequest;

  const toggleOptions = () => setShowOptions((prev) => !prev);

  return (
    <View style={styles.container}>
      <View style={styles.bar}>
        {props.onBackPress ? (
          <TouchableOpacity
            style={styles.backButton}
            onPress={handleBackPress}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={t('topNav.back')}
            testID="activity-header-back"
          >
            <Icon name="Chevron Left" size={16} color={Colors.black} />
          </TouchableOpacity>
        ) : null}

        {/* Type badge + title + asset subtitle: one tap target into settings */}
        <TouchableOpacity
          style={styles.titleGroup}
          onPress={props.onEditPress}
          disabled={!canOpenSettings}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={t('issue.menu.edit')}
          testID="activity-header-settings"
        >
          <View
            style={[
              styles.typeBadge,
              { backgroundColor: typeColors[typeVariant] },
            ]}
          >
            <TypeIcon variant={typeVariant} size={16} color={Colors.white} />
          </View>
          <View style={styles.titleText}>
            <Text style={styles.title} numberOfLines={1} ellipsizeMode="tail">
              {getProcessedTitle()}
            </Text>
            <Text
              style={styles.subtitle}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {issue.asset?.name || issue.workspace.name}
            </Text>
          </View>
        </TouchableOpacity>

        <View style={styles.rightSection}>
          {props.onCallPress && (
            <TouchableOpacity
              onPress={props.onCallPress}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel={t('issue.menu.call')}
              testID="activity-header-call"
            >
              <Icon name="Call" size={24} color={Colors.black} />
            </TouchableOpacity>
          )}
          {!props.hideMenu && !isAccessRequest && (
            <TouchableOpacity
              onPress={toggleOptions}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel={t('issue.menu.options')}
              testID="activity-header-menu"
            >
              <Icon name="More Vertical" size={16} color={Colors.black} />
            </TouchableOpacity>
          )}
        </View>
      </View>
      {showOptions && (
        <ActivityOptionsSheet
          issue={issue}
          mediaUrl={props.mediaUrl}
          onClose={() => setShowOptions(false)}
          onAddMember={props.onAddMember}
          onInvitePress={
            props.onInvitePress
              ? () => {
                  setShowOptions(false);
                  props.onInvitePress?.();
                }
              : undefined
          }
        />
      )}
      <IssueHeaderReminder issue={issue} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: ccColors.neutral[100],
    zIndex: 10,
    ...Platform.select({
      web: {
        zIndex: 1,
      },
    }),
  },
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 48,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  backButton: {
    marginRight: 4,
    padding: 2,
  },
  titleGroup: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    minWidth: 0,
  },
  typeBadge: {
    width: 32,
    height: 32,
    borderRadius: 99,
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleText: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0,
    marginLeft: 4,
  },
  title: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '600',
    color: ccColors.neutral[800],
  },
  subtitle: {
    fontSize: 10,
    lineHeight: 14,
    fontWeight: '400',
    color: ccColors.neutral[500],
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginLeft: 8,
  },
});
